import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AppHeader } from "@/components/app/AppHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { cn } from "@/lib/utils";
import {
  applyLeave,
  workingDaysBetween,
  type LeaveType,
  type HalfDaySession,
} from "@/lib/leave";
import { todayIstIso } from "@/lib/tz";

export const Route = createFileRoute("/_app/leave/apply")({
  component: ApplyLeavePage,
  head: () => ({ meta: [{ title: "Apply for Leave" }] }),
});

const Pill = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "h-8 rounded-full px-4 text-xs font-medium transition-colors",
      active ? "bg-black text-white" : "bg-white text-foreground border border-border hover:bg-muted",
    )}
  >{children}</button>
);

function ApplyLeavePage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [leaveType, setLeaveType] = useState<LeaveType>("paid_full_day");
  const [halfDay, setHalfDay] = useState<HalfDaySession>("first_half");
  const today = todayIstIso();
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [reason, setReason] = useState("");
  const [tasksDuringLeave, setTasksDuringLeave] = useState("");
  const [backupPerson, setBackupPerson] = useState("");

  useEffect(() => {
    if (leaveType === "paid_half_day") setTo(from);
  }, [leaveType, from]);

  const totalDays = useMemo(() => {
    if (leaveType === "paid_half_day") return 0.5;
    if (!from || !to) return 0;
    return workingDaysBetween(from, to);
  }, [from, to, leaveType]);

  const submit = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Not authenticated");
      if (totalDays <= 0) throw new Error("Invalid dates");
      if (reason.trim().length < 10) throw new Error("Reason must be at least 10 characters");
      if (from < today) throw new Error("From date cannot be in the past");
      if (to < from) throw new Error("To date must be on or after From date");
      await applyLeave({
        employee_id: user.id,
        leave_type: leaveType,
        half_day_session: leaveType === "paid_half_day" ? halfDay : null,
        from_date: from,
        to_date: leaveType === "paid_half_day" ? from : to,
        total_days: totalDays,
        reason: reason.trim(),
        tasks_during_leave: tasksDuringLeave.trim() || null,
        backup_person: backupPerson.trim() || null,
      });
    },
    onSuccess: () => {
      toast.success("Leave request submitted. HR will review it shortly.");
      qc.invalidateQueries({ queryKey: ["my-leaves"] });
      navigate({ to: "/leave/my-leaves" });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <>
      <AppHeader subtitle="Submit a leave request for HR approval" />
      <div className="p-6 space-y-4 max-w-2xl">
        <Card>
          <CardContent className="p-5 space-y-4">
            <div>
              <Label className="text-xs">Leave Type *</Label>
              <div className="mt-2 flex gap-2">
                <Pill active={leaveType === "paid_full_day"} onClick={() => setLeaveType("paid_full_day")}>Full Day</Pill>
                <Pill active={leaveType === "paid_half_day"} onClick={() => setLeaveType("paid_half_day")}>Half Day</Pill>
              </div>
            </div>

            {leaveType === "paid_half_day" && (
              <div>
                <Label className="text-xs">Session</Label>
                <div className="mt-2 flex gap-2">
                  <Pill active={halfDay === "first_half"} onClick={() => setHalfDay("first_half")}>First Half (Morning)</Pill>
                  <Pill active={halfDay === "second_half"} onClick={() => setHalfDay("second_half")}>Second Half (Afternoon)</Pill>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">From Date *</Label>
                <Input type="date" value={from} min={today} onChange={(e) => setFrom(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">To Date *</Label>
                <Input
                  type="date"
                  value={leaveType === "paid_half_day" ? from : to}
                  min={from}
                  disabled={leaveType === "paid_half_day"}
                  onChange={(e) => setTo(e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Total Days</Label>
              <div className="mt-1 text-sm font-semibold">{totalDays.toFixed(1)} days</div>
            </div>

            <div>
              <Label className="text-xs">Reason *</Label>
              <Textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Briefly describe why you need this leave..."
              />
              <p className="mt-1 text-[10px] text-muted-foreground">{reason.trim().length} / 10 minimum</p>
            </div>

            <div>
              <Label className="text-xs">Tasks During Leave (optional)</Label>
              <Textarea
                rows={2}
                value={tasksDuringLeave}
                onChange={(e) => setTasksDuringLeave(e.target.value)}
                placeholder="What work is pending / in queue?"
              />
            </div>

            <div>
              <Label className="text-xs">Backup Person (optional)</Label>
              <Input
                value={backupPerson}
                onChange={(e) => setBackupPerson(e.target.value)}
                placeholder="Who will handle urgent work?"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => navigate({ to: "/dashboard" })}>Cancel</Button>
              <Button
                className="bg-black text-white hover:bg-black/90"
                onClick={() => submit.mutate()}
                disabled={submit.isPending}
              >
                {submit.isPending ? "Submitting..." : "Submit Request →"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
